import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import html2pdf from 'html2pdf.js';
import logo from '../assets/imgs/logo.png';
import { API_ORIGIN } from '../services/server';
import { statusPedidoNome } from '../helper/helpers';

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '-';

const formatCep = (value) => {
  const d = String(value ?? '').replace(/\D/g, '');
  if (d.length === 8) return `${d.slice(0, 5)}-${d.slice(5)}`;
  return String(value ?? '').trim() || '';
};

/** Celular (11) ou fixo (10); aceita +55 ou caracteres misturados. */
const formatTelefone = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  let d = raw.replace(/\D/g, '');
  if (d.startsWith('55') && d.length > 11) d = d.slice(2);
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  if (d.length === 9) return `${d.slice(0, 5)}-${d.slice(5)}`;
  if (d.length === 8) return `${d.slice(0, 4)}-${d.slice(4)}`;
  return raw;
};

const textoContatoCliente = (cliente) => {
  const tel = cliente?.telefone;
  const cel = cliente?.celular;
  const email = cliente?.email;
  if (tel) return formatTelefone(tel);
  if (cel) return formatTelefone(cel);
  if (email) return String(email);
  return '—';
};

/** Insumos do item para o PDF (chave + valor do catálogo). */
function linhasInsumosItem(item) {
  const rows = item?.composicaoItemPedidos;
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const out = [];
  for (const row of rows) {
    const ins = row?.insumo;
    if (!ins) continue;
    const chave = String(ins.chave || '').trim();
    const valor = String(ins.valor || '').trim();
    if (chave && valor) out.push(`${chave}: ${valor}`);
    else if (valor) out.push(valor);
    else if (chave) out.push(chave);
  }
  return out;
}

/** Endereço no PDF: rua, CEP, bairro e cidade (pedido) ou texto legado do cliente. */
function linhasEnderecoEntrega(pedido, cliente) {
  const e = pedido?.endereco;
  if (e && typeof e === 'object') {
    const linhas = [];
    const rua = [e.logradouro, e.numero].filter(Boolean).join(', ').trim();
    const cepFmt = formatCep(e.cep);
    if (cepFmt) linhas.push(`CEP ${cepFmt}`);
    if (rua) linhas.push(rua);
    if (e.bairro) linhas.push(String(e.bairro).trim());
    if (e.cidade) linhas.push(String(e.cidade).trim());
    if (linhas.length) return linhas;
  }
  const legado = String(cliente?.endereco || '').trim();
  if (legado) return [legado];
  return null;
}

/** Mesma regra de `PedidoImagesTab` — garante URL pública para o PDF. */
function urlPublicaArquivo(a, pedidoId) {
  if (!a) return '';
  if (a.url) return a.url;

  let p = String(a.path || '').replace(/\\/g, '/');
  if (p.startsWith('local:')) {
    p = p.slice('local:'.length);
  }
  if (pedidoId && p.startsWith('pedido-imagens/')) {
    p = `pedido-${pedidoId}/${p.slice('pedido-imagens/'.length)}`;
  }
  if (p) return `${API_ORIGIN}/uploads/${p}`;
  return '';
}

const PedidoPdf = forwardRef(({ pedido }, ref) => {
  const printRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      gerarPdf: async () => {
        if (!pedido || !printRef.current) return;

        const options = {
          margin: [12, 12, 12, 12],
          filename: `pedido-${pedido?.id || 'documento'}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 1.85, useCORS: true, logging: false },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        };

        const worker = html2pdf().set(options).from(printRef.current);
        const pdfBlob = await worker.outputPdf('blob');
        const blobUrl = URL.createObjectURL(pdfBlob);
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      },
    }),
    [pedido],
  );

  const itens = pedido?.pedidoItems || pedido?.item || [];
  const cliente = pedido?.cliente || pedido?.clienteDados || pedido?.cliente_info || {};
  const observacoesTexto = (pedido?.observacoes ?? pedido?.observacao ?? '').trim();

  const arquivosBrutos = Array.isArray(pedido?.arquivo)
    ? pedido.arquivo
    : Array.isArray(pedido?.arquivos)
      ? pedido.arquivos
      : [];

  const imagensPedido = arquivosBrutos.filter(
    (a) => !a?.tipo || String(a.tipo).toLowerCase().startsWith('image/'),
  );

  const statusLegivel = pedido?.status
    ? statusPedidoNome[pedido.status] || pedido.status
    : '—';

  const valorPedido = Number(pedido?.valorTotal ?? pedido?.total ?? 0);
  const totalRecebidoNum = Number(pedido?.totalRecebido ?? 0);
  const listaPagamentos = Array.isArray(pedido?.pagamentos) ? pedido.pagamentos : [];
  const temPagamentoInformado = listaPagamentos.length > 0 || totalRecebidoNum > 0;
  const faltaReceber = Math.max(0, valorPedido - totalRecebidoNum);
  const enderecoLinhas = linhasEnderecoEntrega(pedido, cliente);

  /** Rosa da identidade — apenas linhas (faixa superior, sublinhados de seção, rodapé). */
  const linePink = '#e91e63';
  const titleMuted = '#475569';
  const muted = '#64748b';

  const styles = {
    root: {
      position: 'fixed',
      inset: 0,
      opacity: 0,
      pointerEvents: 'none',
      zIndex: -1,
    },
    page: {
      width: '720px',
      maxWidth: '720px',
      background: '#fff',
      color: '#0f172a',
      fontFamily: '"Segoe UI", Arial, sans-serif',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      overflow: 'hidden',
      boxSizing: 'border-box',
    },
    topBar: {
      height: '4px',
      background: linePink,
    },
    inner: {
      padding: '12px 20px 14px',
    },
    logoWrap: {
      marginBottom: '8px',
    },
    logoImg: {
      display: 'block',
      maxHeight: '36px',
      width: 'auto',
      maxWidth: '160px',
      objectFit: 'contain',
    },
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '12px',
      gap: '10px',
    },
    heading: { fontSize: '15px', fontWeight: 700, margin: 0, color: '#0f172a', lineHeight: 1.2 },
    meta: { fontSize: '11px', color: '#475569', marginTop: '2px' },
    statusCol: { textAlign: 'right', flexShrink: 0 },
    statusLabel: { fontSize: '9px', fontWeight: 600, color: muted, textTransform: 'uppercase', marginBottom: '3px' },
    badge: {
      display: 'inline-block',
      padding: '4px 9px',
      borderRadius: '4px',
      background: '#f1f5f9',
      border: '1px solid #e2e8f0',
      fontSize: '10px',
      fontWeight: 600,
      color: '#334155',
    },
    section: { marginBottom: '18px' },
    sectionTitle: {
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: titleMuted,
      marginBottom: '10px',
      paddingBottom: '6px',
      borderBottom: `2px solid ${linePink}`,
    },
    clientBox: {
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      padding: '12px 14px',
    },
    clientSplit: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '20px',
    },
    clientColMain: {
      flex: '1 1 0',
      minWidth: 0,
    },
    clientColAddr: {
      flex: '0 1 48%',
      minWidth: '180px',
      maxWidth: '320px',
      paddingLeft: '12px',
      borderLeft: '1px solid #e2e8f0',
    },
    addrTitle: {
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: titleMuted,
      marginBottom: '8px',
    },
    addrLine: { fontSize: '12px', lineHeight: 1.45, color: '#334155', margin: '2px 0' },
    payBox: {
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      overflow: 'hidden',
      background: '#fff',
    },
    payRowTotal: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px',
      margin: 0,
      padding: '10px',
      fontSize: '12px',
      fontWeight: 700,
      background: '#f8fafc',
      color: '#334155',
      borderBottom: '1px solid #e2e8f0',
    },
    payRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '12px',
      margin: 0,
      padding: '10px',
      fontSize: '13px',
      color: '#334155',
    },
    payValue: { fontWeight: 700, color: '#334155' },
    text: { fontSize: '13px', margin: '4px 0', lineHeight: 1.45, color: '#334155' },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '12px',
      marginTop: '4px',
      border: '1px solid #e2e8f0',
    },
    th: {
      textAlign: 'left',
      padding: '10px 10px',
      background: '#f1f5f9',
      borderBottom: '1px solid #e2e8f0',
      fontWeight: 600,
      color: '#475569',
    },
    td: { padding: '10px', borderBottom: '1px solid #e2e8f0', color: '#334155' },
    tdRight: { textAlign: 'right' },
    totalRow: { fontWeight: 700, background: '#f8fafc' },
    obsBox: {
      background: '#fffbeb',
      border: '1px solid #fde68a',
      borderRadius: '6px',
      padding: '12px 14px',
      fontSize: '13px',
      lineHeight: 1.5,
      color: '#422006',
      whiteSpace: 'pre-wrap',
    },
    imgGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      marginTop: '8px',
    },
    imgWrap: {
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      padding: '6px',
      background: '#f8fafc',
      width: '160px',
      boxSizing: 'border-box',
    },
    img: {
      width: '100%',
      height: '140px',
      objectFit: 'contain',
      display: 'block',
    },
    imgCaption: {
      fontSize: '10px',
      color: muted,
      marginTop: '6px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    footer: {
      marginTop: '22px',
      paddingTop: '12px',
      borderTop: `1px solid ${linePink}`,
      fontSize: '10px',
      color: '#94a3b8',
      textAlign: 'center',
    },
  };

  return (
    <div style={styles.root}>
      <div ref={printRef} style={styles.page}>
        <div style={styles.topBar} aria-hidden />
        <div style={styles.inner}>
          <div style={styles.logoWrap}>
            <img src={logo} alt="" style={styles.logoImg} />
          </div>
          <header style={styles.headerRow}>
            <div>
              <h6 style={styles.heading}>Pedido nº {pedido?.id ?? '—'}</h6>
              <div style={styles.meta}>
                <span>Emitido em {formatDate(pedido?.dataCriacao || pedido?.data)}</span>
              </div>
            </div>
            <div style={styles.statusCol}>
              <div style={styles.statusLabel}>Status</div>
              <span style={styles.badge}>{statusLegivel}</span>
            </div>
          </header>

          <section style={styles.section}>
            <div style={styles.sectionTitle}>Dados do cliente</div>
            <div style={styles.clientBox}>
              <div
                style={{
                  ...styles.clientSplit,
                  ...(enderecoLinhas ? {} : { justifyContent: 'flex-start' }),
                }}
              >
                <div style={styles.clientColMain}>
                  <div style={styles.text}>
                    <strong>Nome:</strong> {cliente?.nome || cliente?.razaoSocial || '—'}
                  </div>
                  <div style={styles.text}>
                    <strong>Documento:</strong> {cliente?.documento || cliente?.cnpj || '—'}
                  </div>
                  <div style={styles.text}>
                    <strong>Contato:</strong> {textoContatoCliente(cliente)}
                  </div>
                </div>
                {enderecoLinhas ? (
                  <div style={styles.clientColAddr}>
                    <div style={styles.addrTitle}>Endereco</div>
                    {enderecoLinhas.map((linha, idx) => (
                      <div key={idx} style={styles.addrLine}>
                        {linha}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </section>

          <section style={styles.section}>
            <div style={styles.sectionTitle}>Itens do pedido</div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Descrição</th>
                  <th style={{ ...styles.th, width: '52px' }}>Qtd</th>
                  <th style={{ ...styles.th, ...styles.tdRight, width: '100px' }}>Valor unit.</th>
                  <th style={{ ...styles.th, ...styles.tdRight, width: '100px' }}>Valor total</th>
                </tr>
              </thead>
              <tbody>
                {itens.length === 0 && (
                  <tr>
                    <td style={styles.td} colSpan={4}>
                      Nenhum item informado.
                    </td>
                  </tr>
                )}
                {itens.map((item) => (
                  <tr key={item.id || item.nomeItem}>
                    <td style={styles.td}>{item?.nomeItem || item?.nome || 'Item'}</td>
                    <td style={styles.td}>{item?.quantidade || item?.qtd || 0}</td>
                    <td style={{ ...styles.td, ...styles.tdRight }}>{formatCurrency(item?.valorUnitario)}</td>
                    <td style={{ ...styles.td, ...styles.tdRight }}>{formatCurrency(item?.valorTotal)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={styles.totalRow}>
                  <td style={styles.td} colSpan={3}>
                    Subtotal
                  </td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>{formatCurrency(pedido?.subTotal)}</td>
                </tr>
                <tr style={styles.totalRow}>
                  <td style={styles.td} colSpan={3}>
                    Valor total
                  </td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>
                    {formatCurrency(pedido?.valorTotal || pedido?.total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </section>

          {temPagamentoInformado ? (
            <section style={styles.section}>
              <div style={styles.sectionTitle}>Pagamentos</div>
              <div style={styles.payBox}>
                <div style={styles.payRowTotal}>
                  <span>Valor pago (total recebido)</span>
                  <span style={styles.payValue}>{formatCurrency(totalRecebidoNum)}</span>
                </div>
                <div style={styles.payRow}>
                  <span>Falta receber</span>
                  <span
                    style={{
                      ...styles.payValue,
                      color: faltaReceber > 0 ? '#b45309' : '#166534',
                    }}
                  >
                    {formatCurrency(faltaReceber)}
                  </span>
                </div>
              </div>
            </section>
          ) : null}

          {observacoesTexto ? (
            <section style={styles.section}>
              <div style={styles.sectionTitle}>Observações</div>
              <div style={styles.obsBox}>{observacoesTexto}</div>
            </section>
          ) : null}

          {imagensPedido.length > 0 ? (
            <section style={styles.section}>
              <div style={styles.sectionTitle}>Imagens do pedido</div>
              <div style={styles.imgGrid}>
                {imagensPedido.map((img) => {
                  const src = urlPublicaArquivo(img, pedido?.id);
                  if (!src) return null;
                  return (
                    <div key={img.id || img.nome} style={styles.imgWrap}>
                      <img src={src} alt={img.nome || 'Imagem do pedido'} style={styles.img} />
                      {img.nome ? <div style={styles.imgCaption} title={img.nome}>{img.nome}</div> : null}
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}

          <footer style={styles.footer}>
            Documento gerado eletronicamente — apenas para conferência dos dados do pedido.
          </footer>
        </div>
      </div>
    </div>
  );
});

PedidoPdf.displayName = 'PedidoPdf';

export default PedidoPdf;
